/**
 * PostHog browser analytics adapter.
 *
 * Single initialisation point for the whole app (guarded so hot reloads and
 * repeated route renders can never double-initialise, which would duplicate
 * pageviews). Every capture is fire-and-forget: analytics must never slow
 * down or break commerce flows.
 */
import type { PostHog } from "posthog-js";
import { getPostHogConfig } from "./posthog.functions";

let client: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;
const queue: Array<(ph: PostHog) => void> = [];

declare global {
  // eslint-disable-next-line no-var
  var __cb16_posthog_started: boolean | undefined;
}

function flush(ph: PostHog) {
  while (queue.length) {
    const job = queue.shift();
    try {
      job?.(ph);
    } catch {
      /* never throw from analytics */
    }
  }
}

/** Initialise PostHog exactly once per browser session. */
export function initPostHog(): Promise<PostHog | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (initPromise) return initPromise;
  if (globalThis.__cb16_posthog_started) return Promise.resolve(client);
  globalThis.__cb16_posthog_started = true;

  initPromise = (async () => {
    try {
      const cfg = await getPostHogConfig();
      if (!cfg.enabled) {
        console.warn("[posthog] disabled — POSTHOG_API_KEY is not configured");
        return null;
      }
      const { default: posthog } = await import("posthog-js");
      posthog.init(cfg.apiKey, {
        api_host: cfg.apiHost,
        // SPA-aware pageviews: one $pageview per history change, no duplicates.
        capture_pageview: "history_change",
        capture_pageleave: true,
        autocapture: true,
        // Web Vitals (LCP/CLS/FCP/INP) + performance timings.
        capture_performance: { web_vitals: true },
        // Error tracking ($exception events).
        capture_exceptions: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "[data-ph-mask]",
        },
        disable_session_recording: false,
        persistence: "localStorage+cookie",
        defaults: "2025-05-24",
      });
      client = posthog;
      // Expose for diagnostics / verification in the browser console.
      (window as unknown as { posthog?: PostHog }).posthog = posthog;
      flush(posthog);
      return posthog;
    } catch (err) {
      console.warn("[posthog] initialisation failed", err);
      return null;
    }
  })();

  return initPromise;
}

function withClient(job: (ph: PostHog) => void) {
  if (typeof window === "undefined") return;
  if (client) {
    try {
      job(client);
    } catch {
      /* ignore */
    }
    return;
  }
  queue.push(job);
  void initPostHog();
}

/** Capture a product analytics event. Never throws. */
export function phCapture(name: string, properties: Record<string, unknown> = {}) {
  withClient((ph) => ph.capture(name, properties));
}

/** Associate the current visitor with a known customer. */
export function phIdentify(
  distinctId: string,
  properties: Record<string, unknown> = {},
) {
  if (!distinctId) return;
  withClient((ph) => ph.identify(distinctId, properties));
}

/** Clear identity on sign-out so the next visitor is anonymous. */
export function phReset() {
  withClient((ph) => ph.reset());
}